import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  MenuItem,
  useTheme,
} from "@mui/material";
import {
  PRIMARY_PINK,
  PRIMARY_PINK_DARK,
  TRANSPARENT_PINK_08,
  TRANSPARENT_BLACK_08,
} from "../constants";

function ChessRegistrationModal({ open, onClose, onSubmit, loading }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const menuItemSx = isDark
    ? {
        color: "#ffffff !important",
        "&:hover, &:focus": {
          backgroundColor: "rgba(255, 255, 255, 0.08) !important",
        },
        "&.Mui-selected, &.Mui-selected:hover, &.Mui-selected:focus": {
          backgroundColor: "rgba(233, 30, 99, 0.28) !important",
          color: "#ffffff !important",
        },
      }
    : {};
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    rating: "",
    rating_provider: "USCF",
    uscf_id: "",
  });
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");

    // Validation
    if (!form.first_name.trim()) {
      setError("First name is required");
      return;
    }

    if (!form.last_name.trim()) {
      setError("Last name is required");
      return;
    }

    if (!form.rating || form.rating.trim() === "") {
      setError("Rating is required");
      return;
    }

    const ratingNum = parseInt(form.rating);
    if (isNaN(ratingNum) || ratingNum < 0) {
      setError("Rating must be a valid positive number");
      return;
    }

    if (!form.rating_provider) {
      setError("Rating provider is required");
      return;
    }

    // Submit the form data
    onSubmit({
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      rating: ratingNum,
      rating_provider: form.rating_provider,
      uscf_id: form.uscf_id.trim() || null,
    });
  };

  const handleClose = () => {
    setForm({
      first_name: "",
      last_name: "",
      rating: "",
      rating_provider: "USCF",
      uscf_id: "",
    });
    setError("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h5" component="div">
          Chess Registration Form
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please provide your chess information
        </Typography>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          <TextField
            margin="dense"
            required
            fullWidth
            id="first_name"
            label="First Name"
            name="first_name"
            autoFocus
            value={form.first_name}
            onChange={handleChange}
            placeholder="Enter your first name"
            sx={{ mb: 2 }}
          />

          <TextField
            margin="dense"
            required
            fullWidth
            id="last_name"
            label="Last Name"
            name="last_name"
            value={form.last_name}
            onChange={handleChange}
            placeholder="Enter your last name"
            sx={{ mb: 2 }}
          />

          <TextField
            margin="dense"
            required
            fullWidth
            id="rating"
            label="Rating"
            name="rating"
            type="number"
            value={form.rating}
            onChange={handleChange}
            placeholder="Enter your rating"
            inputProps={{ min: 0 }}
            helperText="Your chess rating"
            sx={{ mb: 2 }}
          />

          <TextField
            margin="dense"
            required
            fullWidth
            select
            id="rating_provider"
            label="Rating Provider"
            name="rating_provider"
            value={form.rating_provider}
            onChange={handleChange}
            sx={{
              mb: 2,
              "& .MuiOutlinedInput-root": {
                height: 56,
                minHeight: 56,
              },
            }}
            SelectProps={{
              sx: {
                height: 56,
                minHeight: 56,
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  boxSizing: "border-box",
                  height: "56px !important",
                  minHeight: "56px !important",
                  padding: "16.5px 32px 16.5px 14px !important",
                  color: isDark ? "#ffffff" : "#000000",
                },
                "& .MuiSelect-icon": {
                  display: "block !important",
                  color: isDark ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.54)",
                },
              },
              MenuProps: {
                PaperProps: {
                  sx: {
                    backgroundColor: isDark ? "#1e1e1e !important" : "#ffffff !important",
                    backgroundImage: "none",
                    color: isDark ? "#ffffff" : "#000000",
                  },
                },
              },
            }}
          >
            <MenuItem value="USCF" sx={menuItemSx}>USCF</MenuItem>
            <MenuItem value="FIDE" sx={menuItemSx}>FIDE</MenuItem>
            <MenuItem value="chess.com" sx={menuItemSx}>chess.com</MenuItem>
            <MenuItem value="lichess.org" sx={menuItemSx}>lichess.org</MenuItem>
            <MenuItem value="other" sx={menuItemSx}>Other</MenuItem>
          </TextField>

          <TextField
            margin="dense"
            fullWidth
            id="uscf_id"
            label="USCF ID (Optional)"
            name="uscf_id"
            value={form.uscf_id}
            onChange={handleChange}
            placeholder="Enter your USCF ID if applicable"
            helperText="Optional: Your USCF membership ID"
            sx={{ mb: 1 }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={loading}
          sx={{
            color: "#000000",
            borderColor: "#000000",
            fontWeight: 600,
            textTransform: "none",
            "&:hover": {
              borderColor: "#000000",
              backgroundColor: TRANSPARENT_BLACK_08,
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          sx={{
            bgcolor: PRIMARY_PINK,
            color: "#FFFFFF",
            fontWeight: 600,
            textTransform: "none",
            "&:hover": {
              bgcolor: PRIMARY_PINK_DARK,
            },
          }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : "Submit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ChessRegistrationModal;
